import React, { useState, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import TrancheTable from "./Calculator10b51/TrancheTable";
import { RadioSelector } from "../utils/Constants/systemSetting";

import AwardsTable from "./Calculator10b51/AwardsTable";
import TrancheSummaryTable from "./Calculator10b51/TrancheSummaryTable.js";
import CombinedSchedule from "./Calculator10b51/CombinedSchedule";
import SimulateStockPrices from "./Calculator10b51/SimulateStockPrices.js";
import StrategyPlanning from "./Calculator10b51/StrategyPlanning.js";
import RetirementDatePlanning from "./Calculator10b51/RetirementDatePlanning.js";

import optionTableRows from "./Calculator10b51/json/optionTableRows.json";
import RSUTableRows from "./Calculator10b51/json/RSUTableRows.json";
import generalInfoRows from "./Calculator10b51/json/generalInfoRows.json";
import taxTableRows from "./Calculator10b51/json/taxTableRows.json";

import defaultTrancheValues from "./Calculator10b51/json/defaultTrancheValues.json";
import defaultOptionValues from "./Calculator10b51/json/defaultOptionValues.json";
import defaultRSUValues from "./Calculator10b51/json/defaultRSUValues.json";
import TaxRateTable from "./Calculator10b51/TaxRateTable.js";
import dayjs from "dayjs";
import { calculateTrancheTableValues } from "./Calculator10b51/_10b51utils.js";
import DailyPricesGraph from "./CompanyFinancials/DailyPricesGraph.js";
import {
  getFiscalData,
  getFiscalStockPrice,
} from "../redux/actions/CompanyAction.js";
import VolatilityCalculator from "./OptionsCalculators/VolatilityCalculator";

export default function Calculator10b51({
  companyInfo,
  PDFprocessing,
  calculatorPages,
  generalInfoParams,
  setGeneralInfoParams,
  taxTableParams,
  setTaxTableParams,
  optionsCalculatorValues,
  setOptionsCalculatorValues,
  RSUCalculatorValues,
  setRSUCalculatorValues,
  trancheTableOptionValues,
  setTrancheTableOptionValues,
  trancheTableRSUValues,
  setTrancheTableRSUValues,
  allTrancheTableValues,
  setAllTrancheTableValues,
  initialized,
  setInitalized,
  firstLoad,
  setFirstLoad,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);
  const { financialData, stockPrice } = useSelector(
    (state) => state.CompanyReducer,
  );

  // Feautre will eventually be available for tier 2 subscribers (subject to change)
  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);

  // For now, access to this feature is restricted to internal users with the compAIaccess property
  const compAIaccess = useMemo(() => {
    if (user?.compAIaccess) {
      return true;
    }
    return false;
  }, [user]);

  const [calculatorPageSelected, setCalculatorPageSelected] =
    useState("Initial Parameters");

  const [updateNeeded, setUpdateNeeded] = useState(false);
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [loadingStockPrices, setLoadingStockPrices] = useState(false);
  const [calculatedPlanValues, setCalculatedPlanValues] = useState(null);
  const [committedSimulationParams, setCommittedSimulationParams] =
    useState(null);
  const [dateFormattedPricePaths, setDateFormattedPricePaths] = useState(null);

  const currentStockPrice = useMemo(() => {
    const currentStockPrice =
      stockPrice?.[companyInfo.Ticker]?.result?.[0]?.ratio;

    if (!isNaN(parseFloat(currentStockPrice))) {
      return parseFloat(currentStockPrice).toFixed(2);
    }
    if (stockPrice?.[companyInfo.Ticker]) {
      return "N/A";
    }
    return null;
  }, [companyInfo, stockPrice]);

  useEffect(() => {
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;
    const token = localStorage.getItem("ACCESS_TOKEN");

    if (ticker && token) {
      //TODO: replace ratio here with price if we pay for stock price module
      if (ticker && !stockPrice?.[companyInfo.Ticker]?.result?.[0]?.ratio) {
        dispatch(
          getFiscalStockPrice(
            "Current Stock Price",
            [ticker],
            [CIK], //CIKs
            null, //periodType
            null, //fiscalYearneeded
            "USD",
            true, //Pulls current stock price only "latest" = true
            token,
          ),
        );
      }

      if (
        ticker &&
        currentStockPrice &&
        currentStockPrice !== "N/A" &&
        !financialData?.["Current Stock Price"]?.[companyInfo?.Ticker]?.result
      ) {
        setLoadingStockPrices(true);
        dispatch(
          getFiscalData(
            "Current Stock Price",
            [ticker],
            [CIK], //CIKs
            null, //periodType
            null, //fiscalYearneeded
            "USD",
            false,
            token,
          ),
        );
      }
      if (
        financialData?.["Current Stock Price"]?.[companyInfo?.Ticker]?.result
      ) {
        setLoadingStockPrices(false);
      }
    }
  }, [companyInfo, financialData, stockPrice, currentStockPrice, dispatch]);

  const [
    lookbackVolatilityBlendedAverage,
    setLookbackVolatilityBlendedAverage,
  ] = useState(null);
  const [calculatingVolatility, setCalculatingVolatility] = useState(true);
  const [showVolatilityEditView, setShowVolatilityEditView] = useState(false);

  useEffect(() => {
    if (generalInfoParams?.[0]?.includeTaxes) {
      setIncludeTaxes(true);
    } else {
      setIncludeTaxes(false);
    }
  }, [generalInfoParams]);

  useEffect(() => {
    if (generalInfoParams?.[0]?.useVolitilityCalculator) {
      setShowVolatilityEditView(true);
    } else {
      setShowVolatilityEditView(false);
    }
  }, [generalInfoParams]);

  // Sets tranche table, RSU table, and Option table values to default values (based on sample data)
  useEffect(() => {
    if (
      allTrancheTableValues?.["Options Schedule"] &&
      !Object.keys(allTrancheTableValues?.["Options Schedule"]).length
    ) {
      setAllTrancheTableValues({ ...defaultTrancheValues });
      setOptionsCalculatorValues(
        [...defaultOptionValues].map((grantDetails) => {
          return Object.fromEntries(
            Object.entries(grantDetails).map(([key, val]) =>
              !["ID", "awardID", "grantDate", "expiryDate"].includes(key)
                ? [key, parseFloat(val)]
                : [key, val],
            ),
          );
        }),
      );
      setRSUCalculatorValues([...defaultRSUValues]);
      setInitalized(true);
    }
  }, [
    allTrancheTableValues,
    setAllTrancheTableValues,
    setOptionsCalculatorValues,
    setRSUCalculatorValues,
    setInitalized,
  ]);

  // // Updates current date if one has not been entered
  // // Note - this doesn't work with treasury yield calculations - that max date can be off by up to a month
  // useEffect(() => {
  //   if (
  //     !generalInfoParams ||
  //     !generalInfoParams.length ||
  //     generalInfoParams?.[0]?.currentDate ||
  //     !currentStockPrice
  //   ) {
  //     return;
  //   }
  //   const newGeneralInfoParams = [...generalInfoParams];
  //   newGeneralInfoParams[0] = {
  //     ...generalInfoParams[0],
  //     currentDate: new Date(),
  //   };
  //   setGeneralInfoParams(newGeneralInfoParams);
  // }, [setGeneralInfoParams, generalInfoParams, currentStockPrice, companyInfo]);

  useEffect(() => {
    if (!lookbackVolatilityBlendedAverage || !currentStockPrice) {
      return;
    }
    setGeneralInfoParams((prev) => {
      const newGeneralInfoParams = [...prev];

      newGeneralInfoParams[0] = {
        ...newGeneralInfoParams[0],
        ticker: companyInfo?.Ticker,
        companyName: companyInfo?.Company,
        volatility: lookbackVolatilityBlendedAverage,
        //TODO: Enable to use current share price of company stock
        sharePrice: currentStockPrice,
      };
      return newGeneralInfoParams;
    });
  }, [
    companyInfo,
    setGeneralInfoParams,
    currentStockPrice,
    showVolatilityEditView,
    lookbackVolatilityBlendedAverage,
  ]);

  const [dividendYieldUpdateNeeded, setDividendYieldUpdateNeeded] =
    useState(true);
  const [prevGeneralInfoParams, setPrevGeneralInfoParams] = useState({});
  useEffect(() => {
    const dividendAmount = generalInfoParams?.[0]?.dividendAmount;
    const sharePrice = generalInfoParams?.[0]?.sharePrice;
    const dividendFrequency =
      generalInfoParams?.[0]?.dividendFrequency || "Quarterly";
    if (
      dividendAmount !== prevGeneralInfoParams?.dividendAmount ||
      sharePrice !== prevGeneralInfoParams?.sharePrice ||
      dividendFrequency !== prevGeneralInfoParams?.dividendFrequency
    ) {
      setPrevGeneralInfoParams(generalInfoParams?.[0]);
      setDividendYieldUpdateNeeded(true);
    }
  }, [generalInfoParams, prevGeneralInfoParams]);

  useEffect(() => {
    if (dividendYieldUpdateNeeded) {
      function calculateDividendYield(dividend, sharePrice, frequency) {
        const annual = frequency === "Monthly" ? dividend * 12 : dividend * 4;
        return ((annual / sharePrice) * 100).toFixed(2);
      }
      setGeneralInfoParams((prev) => {
        const newGeneralInfoParams = [...prev];
        const dividendAmount = newGeneralInfoParams?.[0]?.dividendAmount || 0;
        const sharePrice = newGeneralInfoParams?.[0]?.sharePrice || 0;
        const dividendFrequency =
          newGeneralInfoParams?.[0]?.dividendFrequency || "Quarterly";
        const dividendYield = calculateDividendYield(
          dividendAmount,
          sharePrice,
          dividendFrequency,
        );

        newGeneralInfoParams[0] = {
          ...newGeneralInfoParams[0],
          dividendYield: dividendYield,
        };
        return newGeneralInfoParams;
      });
      setDividendYieldUpdateNeeded(false);
    }
  }, [setGeneralInfoParams, dividendYieldUpdateNeeded]);

  useEffect(() => {
    if (!generalInfoParams?.[0]?.useDefaultVolatility) {
      return;
    }
    setOptionsCalculatorValues((prev) => {
      const newOptionCalculatorValues = [...prev];
      const optionCalculatorLength = newOptionCalculatorValues.length;

      for (let i = 0; i < optionCalculatorLength; i++) {
        newOptionCalculatorValues[i] = {
          ...newOptionCalculatorValues[i],
          volatility: generalInfoParams?.[0]?.volatility,
        };
      }

      return newOptionCalculatorValues;
    });
  }, [
    companyInfo,
    generalInfoParams,
    setOptionsCalculatorValues,
    currentStockPrice,
    showVolatilityEditView,
    lookbackVolatilityBlendedAverage,
  ]);

  // Currently set to grant 2 to ensure sample data gets calculated
  const [trancheScheduleSelected, settrancheScheduleSelected] =
    useState("Grant 2");

  // Populate radio options for tranche table based on award type (Grant 1, 2, 3 etc)
  const trancheScheduleOptions = useMemo(() => {
    const getCurrentAwards = (calculatorPageSelected) => {
      if (calculatorPageSelected === "Options Schedule") {
        return optionsCalculatorValues;
      }
      if (calculatorPageSelected === "RSU Schedule") {
        return RSUCalculatorValues;
      }
      return [];
    };
    const currentAwardValues = getCurrentAwards(calculatorPageSelected);

    return currentAwardValues.map((item) => item.ID.replace("grant", "Grant "));
  }, [RSUCalculatorValues, optionsCalculatorValues, calculatorPageSelected]);

  // previous State varariables used to detect if updates are needed for calculations
  const [prevCalculatorSelected, setPrevCalculatorSelected] = useState("");
  const [prevTrancheScheduleSelected, setPrevTrancheScheduleSelected] =
    useState("");
  const [prevTrancheValues, setPrevTrancheValues] = useState({});

  // Reset to grant 1 when switching between calculator pages
  // This prevents a grant being selected that doesnt exist for a different calculator
  useMemo(() => {
    if (!firstLoad && calculatorPageSelected !== prevCalculatorSelected) {
      settrancheScheduleSelected("Grant 1");
    }
  }, [calculatorPageSelected, prevCalculatorSelected, firstLoad]);

  // Detect changes in the calculator/tranche table or tranche values...
  // and update the option/RSU tranche values accordingly
  useEffect(() => {
    if (
      calculatorPageSelected !== prevCalculatorSelected ||
      trancheScheduleSelected !== prevTrancheScheduleSelected ||
      //Compare values but strip out dividendSchedule as it has hover text JSX table
      JSON.stringify(allTrancheTableValues, (key, value) =>
        key === "dividendSchedule" ? undefined : value,
      ) !==
        JSON.stringify(prevTrancheValues, (key, value) =>
          key === "dividendSchedule" ? undefined : value,
        )
    ) {
      setPrevCalculatorSelected(calculatorPageSelected);
      setPrevTrancheScheduleSelected(trancheScheduleSelected);
      setPrevTrancheValues(allTrancheTableValues);

      if (calculatorPageSelected === "Options Schedule") {
        setTrancheTableOptionValues(
          allTrancheTableValues?.["Options Schedule"]?.[
            trancheScheduleSelected
          ] || [],
        );
      }
      if (calculatorPageSelected === "RSU Schedule") {
        setTrancheTableRSUValues(
          allTrancheTableValues?.["RSU Schedule"]?.[trancheScheduleSelected] ||
            [],
        );
      }
    }
  }, [
    calculatorPageSelected,
    trancheScheduleSelected,
    setTrancheTableOptionValues,
    setTrancheTableRSUValues,
    allTrancheTableValues,
    prevCalculatorSelected,
    prevTrancheScheduleSelected,
    prevTrancheValues,
  ]);

  // Update allTrancheTableValues state based on individual tranche table states
  // allTrancheTableValues is needed for later calculations when awards typses are combined
  useEffect(() => {
    if (initialized) {
      setAllTrancheTableValues((prev) => {
        const newValues = { ...prev };

        if (
          calculatorPageSelected === "Options Schedule" &&
          newValues?.["Options Schedule"]
        ) {
          newValues["Options Schedule"][trancheScheduleSelected] =
            trancheTableOptionValues;
        }
        if (
          calculatorPageSelected === "RSU Schedule" &&
          newValues?.["RSU Schedule"]
        ) {
          newValues["RSU Schedule"][trancheScheduleSelected] =
            trancheTableRSUValues;
        }
        return newValues;
      });
    }
  }, [
    calculatorPageSelected,
    setAllTrancheTableValues,
    trancheScheduleSelected,
    trancheTableOptionValues,
    trancheTableRSUValues,
    initialized,
    firstLoad,
  ]);

  // Complete calculations and valuations for all grants, not just one selected
  // Important: Within the tranche table there is another
  // calculation that updates the state of the current grant selected
  useEffect(() => {
    if (initialized) {
      setAllTrancheTableValues((prev) => {
        for (const [calculatorType, allTrancheValues] of Object.entries(prev)) {
          for (const [grantKey, trancheTableValues] of Object.entries(
            allTrancheValues,
          )) {
            const newTrancheTableValues = [...trancheTableValues];
            if (calculatorType === "Options Schedule") {
              const currentOptionCalculatorValues =
                optionsCalculatorValues.find(
                  (item) => item.ID.replace("grant", "Grant ") === grantKey,
                );
              trancheTableValues.forEach((trancheRowValues, awardIndex) => {
                const calaculatedTrancheTableValues =
                  calculateTrancheTableValues(
                    {
                      ...generalInfoParams?.[0],
                      ...currentOptionCalculatorValues,
                      ...trancheRowValues,
                    },
                    taxTableParams,
                  );
                newTrancheTableValues[awardIndex] = {
                  ...trancheRowValues,
                  ...calaculatedTrancheTableValues,
                };
              });
            } else if (calculatorType === "RSU Schedule") {
              const shortTermTaxRate =
                (taxTableParams.find((item) => item.ID === "Short-Term")
                  ?.totalTax || 0) / 100;
              // To be used as table advances to detect short and long term captial gains
              const longTermTaxRate =
                (taxTableParams.find((item) => item.ID === "Long-Term")
                  ?.totalTax || 0) / 100;
              trancheTableValues.forEach((trancheRowValues, awardIndex) => {
                const remainingUnits = trancheRowValues?.initialUnits;
                const remainingFullValue =
                  remainingUnits * (generalInfoParams?.[0]?.sharePrice || 0);

                const remainingFullValueAfterTax = Math.max(
                  remainingFullValue * (1 - shortTermTaxRate),
                  0,
                );

                const exerciseDate =
                  trancheRowValues?.exerciseDate ||
                  trancheRowValues?.trancheDate
                    ? dayjs(trancheRowValues?.trancheDate)
                        .add(1, "day")
                        .format()
                    : "";
                newTrancheTableValues[awardIndex] = {
                  ...trancheRowValues,
                  remainingUnits,
                  remainingFullValue,
                  remainingFullValueAfterTax,
                  exerciseDate,
                };
              });
            }
            prev[calculatorType][grantKey] = newTrancheTableValues;
          }
        }
        return prev;
      });
    }
  }, [
    initialized,
    allTrancheTableValues,
    setAllTrancheTableValues,
    generalInfoParams,
    taxTableParams,
    optionsCalculatorValues,
    trancheScheduleSelected,
  ]);

  // Update the current ref state for the tranche table for options
  const currentOptionTrancheValues = useRef(
    allTrancheTableValues?.["Options Schedule"],
  );
  useEffect(() => {
    currentOptionTrancheValues.current =
      allTrancheTableValues?.["Options Schedule"];
  }, [allTrancheTableValues]);

  // Update the current ref state for the tranche table for RSUs
  const currentRSUTrancheValues = useRef(
    allTrancheTableValues?.["RSU Schedule"],
  );
  useEffect(() => {
    currentRSUTrancheValues.current = allTrancheTableValues?.["RSU Schedule"];
  }, [allTrancheTableValues]);

  // Reset tranche schedule selected after the initialization calculations are complete
  useEffect(() => {
    function setStateAfterDelay(setter, value, delay = 500) {
      setTimeout(() => setter(value), delay);
    }
    if (initialized && firstLoad) {
      setStateAfterDelay(settrancheScheduleSelected, "Grant 1");
      setFirstLoad(false);
    }
  }, [firstLoad, setFirstLoad, initialized]);

  // TODO: For updating default table values:
  // console.log(allTrancheTableValues);
  // console.log(optionsCalculatorValues);
  // console.log(RSUCalculatorValues);

  return (
    <>
      {/* {!isTier2 ? ( */}
      {!compAIaccess ? (
        <div className="col-lg-12" style={{ height: "100%", padding: 50 }}>
          <h3
            style={{
              textAlign: "center",
              fontWeight: "normal",
              fontSize: 22,
            }}
          >
            {/* <p style={{ fontWeight: "bold" }}>
              This page is for C-Suite Comp&reg; Enterprise users only.
            </p> */}
            <br />
            <p>
              Contact your account manager to access the Equity Compensation
              Modeling (ECM).
            </p>
          </h3>
        </div>
      ) : (
        <>
          <div className="company-calculator-section col-lg-12 p-0">
            <div>
              <h2 style={{ textAlign: "center" }}>
                Equity Compensation Modeling (ECM)
              </h2>
              <RadioSelector
                options={calculatorPages}
                state={calculatorPageSelected}
                setState={setCalculatorPageSelected}
                heading=""
                style={{ justifyContent: "center" }}
                hoverText="Select Award Type"
              />
              {calculatorPageSelected === "Initial Parameters" && (
                <div style={{ marginTop: 20 }}>
                  <h2>
                    {companyInfo?.Company ? `${companyInfo?.Company} ` : ""}
                    Share Price
                  </h2>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 15,
                    }}
                  >
                    <div style={{ width: "80%" }}>
                      <DailyPricesGraph
                        companyInfo={companyInfo}
                        loadingStockPrices={loadingStockPrices}
                      />
                    </div>
                  </div>
                  <h3>General Info</h3>
                  <AwardsTable
                    companyInfo={companyInfo}
                    awardTitle={"General Info"}
                    paramTableRows={generalInfoRows}
                    awardCalculatorValues={generalInfoParams}
                    setAwardCalculatorValues={setGeneralInfoParams}
                    headless={true}
                  />
                  {includeTaxes && (
                    <div>
                      <h3>Tax Details</h3>
                      <TaxRateTable
                        companyInfo={companyInfo}
                        awardTitle={"Tax Rate"}
                        paramTableRows={taxTableRows}
                        awardCalculatorValues={taxTableParams}
                        setAwardCalculatorValues={setTaxTableParams}
                        // headless={true}
                      />
                    </div>
                  )}
                  <VolatilityCalculator
                    companyInfo={companyInfo}
                    showVolatilityEditView={showVolatilityEditView}
                    lookbackVolatilityBlendedAverage={
                      lookbackVolatilityBlendedAverage
                    }
                    setLookbackVolatilityBlendedAverage={
                      setLookbackVolatilityBlendedAverage
                    }
                    calculatingVolatility={calculatingVolatility}
                    setCalculatingVolatility={setCalculatingVolatility}
                    style={{ height: 250, tableSize: "small" }}
                  />
                </div>
              )}

              {(calculatorPageSelected === "Options Schedule" ||
                calculatorPageSelected === "RSU Schedule") && (
                <div style={{ marginTop: 15 }}>
                  <h3>Adjust General Info</h3>
                  <AwardsTable
                    companyInfo={companyInfo}
                    awardTitle={"General Info"}
                    paramTableRows={generalInfoRows}
                    awardCalculatorValues={generalInfoParams}
                    setAwardCalculatorValues={setGeneralInfoParams}
                    headless={true}
                    abbreviatedTable={true}
                    calculatorPageSelected={calculatorPageSelected}
                  />
                </div>
              )}

              <div>
                {calculatorPageSelected === "Options Schedule" && (
                  <h3>Options Parameters</h3>
                )}
                <AwardsTable
                  companyInfo={companyInfo}
                  awardTitle={"Option"}
                  paramTableRows={optionTableRows}
                  awardCalculatorValues={optionsCalculatorValues}
                  setAwardCalculatorValues={setOptionsCalculatorValues}
                  generalInfoParams={generalInfoParams}
                  taxTableParams={taxTableParams}
                  trancheTableValues={{
                    ...currentOptionTrancheValues.current,
                  }}
                  updateNeeded={updateNeeded}
                  setUpdateNeeded={setUpdateNeeded}
                  trancheScheduleSelected={trancheScheduleSelected}
                  calculatorPageSelected={calculatorPageSelected}
                  setAllTrancheTableValues={setAllTrancheTableValues}
                  settrancheScheduleSelected={settrancheScheduleSelected}
                  includeTaxes={includeTaxes}
                  hidden={calculatorPageSelected !== "Options Schedule"}
                  currentStockPrice={currentStockPrice}
                />
              </div>
              <div>
                {calculatorPageSelected === "RSU Schedule" && (
                  <h3>RSU Parameters</h3>
                )}
                <AwardsTable
                  companyInfo={companyInfo}
                  awardTitle={"RSU"}
                  paramTableRows={RSUTableRows}
                  awardCalculatorValues={RSUCalculatorValues}
                  setAwardCalculatorValues={setRSUCalculatorValues}
                  generalInfoParams={generalInfoParams}
                  taxTableParams={taxTableParams}
                  trancheTableValues={{
                    ...currentRSUTrancheValues.current,
                  }}
                  updateNeeded={updateNeeded}
                  setUpdateNeeded={setUpdateNeeded}
                  trancheScheduleSelected={trancheScheduleSelected}
                  calculatorPageSelected={calculatorPageSelected}
                  setAllTrancheTableValues={setAllTrancheTableValues}
                  settrancheScheduleSelected={settrancheScheduleSelected}
                  includeTaxes={includeTaxes}
                  hidden={calculatorPageSelected !== "RSU Schedule"}
                />
              </div>
            </div>
          </div>

          <>
            <div className="col-lg-12 mt-2">
              {["Options Schedule", "RSU Schedule"].includes(
                calculatorPageSelected,
              ) && (
                <div>
                  <h3>Vesting Schedule</h3>
                  <RadioSelector
                    options={trancheScheduleOptions}
                    state={trancheScheduleSelected}
                    setState={settrancheScheduleSelected}
                    style={{ justifyContent: "center" }}
                    heading=""
                    hoverText=""
                  />
                </div>
              )}
              <TrancheTable
                trancheTableValues={trancheTableOptionValues}
                setTrancheTableValues={setTrancheTableOptionValues}
                generalInfoParams={generalInfoParams}
                optionsCalculatorValues={optionsCalculatorValues}
                calculatorPageSelected={calculatorPageSelected}
                currentAwardValues={optionsCalculatorValues}
                hidden={calculatorPageSelected !== "Options Schedule"}
                trancheScheduleSelected={trancheScheduleSelected}
                prevTrancheScheduleSelected={prevTrancheScheduleSelected}
                taxTableParams={taxTableParams}
                includeTaxes={includeTaxes}
              />
              <TrancheTable
                trancheTableValues={trancheTableRSUValues}
                setTrancheTableValues={setTrancheTableRSUValues}
                generalInfoParams={generalInfoParams}
                optionsCalculatorValues={optionsCalculatorValues}
                calculatorPageSelected={calculatorPageSelected}
                currentAwardValues={RSUCalculatorValues}
                hidden={calculatorPageSelected !== "RSU Schedule"}
                trancheScheduleSelected={trancheScheduleSelected}
                prevTrancheScheduleSelected={prevTrancheScheduleSelected}
                taxTableParams={taxTableParams}
                includeTaxes={includeTaxes}
              />
            </div>
            {["Options Schedule", "RSU Schedule"].includes(
              calculatorPageSelected,
            ) && (
              <div className="col-lg-12 mt-5">
                <h3>Combined {calculatorPageSelected}</h3>
                <TrancheSummaryTable
                  calculatorPageSelected={calculatorPageSelected}
                  allTrancheTableValues={allTrancheTableValues}
                  optionsCalculatorValues={optionsCalculatorValues}
                  RSUCalculatorValues={RSUCalculatorValues}
                  generalInfoParams={generalInfoParams}
                  includeTaxes={includeTaxes}
                />
              </div>
            )}
          </>
          {/* )} */}
          {calculatorPageSelected === "Combined Schedule" && (
            <div>
              <h3>Combined Vesting Schedule</h3>
              <CombinedSchedule
                companyInfo={companyInfo}
                calculatorPages={calculatorPages}
                allTrancheTableValues={allTrancheTableValues}
                optionsCalculatorValues={optionsCalculatorValues}
                RSUCalculatorValues={RSUCalculatorValues}
                generalInfoParams={generalInfoParams}
                setGeneralInfoParams={setGeneralInfoParams}
                setUpdateNeeded={setUpdateNeeded}
                includeTaxes={includeTaxes}
                currentStockPrice={currentStockPrice}
              />
            </div>
          )}
          {calculatorPageSelected === "Simulation & Strategies" && (
            <>
              <div style={{ marginTop: 15 }}>
                <h1 style={{ textAlign: "center" }}>Simulation</h1>

                <h3>Simulation Parameters</h3>
                <AwardsTable
                  companyInfo={companyInfo}
                  awardTitle={"General Info"}
                  paramTableRows={generalInfoRows}
                  awardCalculatorValues={generalInfoParams}
                  setAwardCalculatorValues={setGeneralInfoParams}
                  headless={true}
                  calculatorPageSelected={calculatorPageSelected}
                />
                <div style={{ textAlign: "center" }}>
                  {generalInfoParams?.[0]?.sampleRate === "Daily" && (
                    <div>
                      Warning: Daily sampling rates can take a long time to
                      simulate and can slow down the browser.
                    </div>
                  )}
                  {generalInfoParams?.[0]?.iterations > 100 && (
                    <div>
                      {`Warning: >100 iterations can take a long time to
                      simulate and can slow down the browser.`}
                    </div>
                  )}
                  {parseInt(generalInfoParams?.[0]?.timeHorizon) > 10 && (
                    <div>
                      {`Warning: Time horizons >10 years can take a
                      long time to simulate.`}
                    </div>
                  )}
                </div>
              </div>
              <SimulateStockPrices
                companyInfo={companyInfo}
                generalInfoParams={generalInfoParams}
                committedSimulationParams={committedSimulationParams}
                setCommittedSimulationParams={setCommittedSimulationParams}
                setDateFormattedPricePaths={setDateFormattedPricePaths}
                RSUCalculatorValues={RSUCalculatorValues}
              />

              <StrategyPlanning
                companyInfo={companyInfo}
                generalInfoParams={generalInfoParams}
                calculatedPlanValues={calculatedPlanValues}
                setCalculatedPlanValues={setCalculatedPlanValues}
                optionsCalculatorValues={optionsCalculatorValues}
                RSUCalculatorValues={RSUCalculatorValues}
                allTrancheTableValues={allTrancheTableValues}
                taxTableParams={taxTableParams}
                committedSimulationParams={committedSimulationParams}
                dateFormattedPricePaths={dateFormattedPricePaths}
              />
            </>
          )}
          {calculatorPageSelected === "Retirement Date (Coming Soon)" && (
            <RetirementDatePlanning />
          )}
        </>
      )}
    </>
  );
}
